import React, { useEffect, useState } from "react";
import axios from "axios";
import { X, Loader2, History, ArrowRight, MessageSquare, User } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Right-side slide-in drawer showing the chain-of-custody for a draft's
 * RN assignments. Reads `/api/intake/drafts/{id}/audit` on open. Empty
 * chain renders a friendly "No reassignments — original assignment
 * intact" instead of a blank list.
 */
export default function AuditDrawer({ open, draft, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (!open || !draft?.draft_id) {
      setData(null);
      setErr("");
      return;
    }
    setLoading(true);
    setErr("");
    setData(null);
    axios
      .get(`${API}/intake/drafts/${encodeURIComponent(draft.draft_id)}/audit`)
      .then((res) => setData(res.data))
      .catch((e) => setErr(e?.response?.data?.detail || e?.message || "Could not load audit trail."))
      .finally(() => setLoading(false));
  }, [open, draft?.draft_id]);

  if (!open || !draft) return null;

  const fmtTs = (iso) => {
    if (!iso) return "—";
    try {
      const d = new Date(iso);
      return d.toLocaleString(undefined, {
        month: "short", day: "numeric", year: "numeric",
        hour: "numeric", minute: "2-digit",
      });
    } catch {
      return iso;
    }
  };

  return (
    <div
      className="fixed inset-0 z-40 bg-[#0B1E3A]/30"
      data-testid="audit-drawer-backdrop"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <aside
        className="absolute top-0 right-0 h-full w-full sm:w-[440px] bg-white shadow-2xl flex flex-col"
        data-testid="audit-drawer"
      >
        <header className="flex items-start justify-between px-5 py-4 border-b border-[#E5EEF5]">
          <div className="min-w-0">
            <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-[0.14em] font-semibold text-[#3A5275]">
              <History className="w-3.5 h-3.5" /> Reassignment audit
            </div>
            <h2 className="font-display text-lg font-bold text-[#0B1E3A] mt-1 truncate">
              {draft.client_name || "(unnamed client)"}
            </h2>
            <div className="text-[11px] text-[#3A5275] mt-0.5 font-mono truncate">
              {draft.draft_id}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-[#3A5275] hover:text-[#0B1E3A] shrink-0 ml-3"
            data-testid="audit-close"
          >
            <X className="w-5 h-5" />
          </button>
        </header>

        <div className="flex-1 overflow-y-auto px-5 py-4">
          {loading && (
            <div
              className="flex items-center gap-2 text-[13px] text-[#3A5275] py-8 justify-center"
              data-testid="audit-loading"
            >
              <Loader2 className="w-4 h-4 animate-spin" /> Loading audit trail…
            </div>
          )}

          {!loading && err && (
            <div
              className="text-[12.5px] text-[#A42A2A] px-3 py-2 rounded-lg bg-[#FBECEC] border border-[#E6B8B8]"
              data-testid="audit-error"
            >
              {err}
            </div>
          )}

          {!loading && !err && data && (
            <>
              {/* Current owner */}
              <div className="rounded-xl border border-[#B7D1EA] bg-[#EEF5FC] px-4 py-3 mb-4">
                <div className="text-[10.5px] uppercase tracking-[0.12em] font-semibold text-[#0A3D79] mb-1">
                  Currently assigned to
                </div>
                <div
                  className="flex items-center gap-1.5 text-[14px] font-mono text-[#0B1E3A]"
                  data-testid="audit-current-rn"
                >
                  <User className="w-4 h-4 text-[#0A3D79]" />
                  {data.current_rn || "(unassigned)"}
                </div>
                <div className="text-[11px] text-[#3A5275] mt-1">
                  Status: <span className="font-semibold">{data.status || "—"}</span>
                </div>
              </div>

              {/* Reassignment list */}
              {data.count === 0 ? (
                <div
                  className="rounded-lg border border-[#D6E3EF] bg-[#F7FAFD] px-4 py-6 text-center"
                  data-testid="audit-empty"
                >
                  <History className="w-8 h-8 mx-auto text-[#8FB6D9] mb-2" />
                  <div className="text-[13px] font-semibold text-[#0B1E3A]">
                    No reassignments yet
                  </div>
                  <div className="text-[11.5px] text-[#3A5275] mt-1">
                    Original assignment intact.
                  </div>
                </div>
              ) : (
                <>
                  <div className="text-[10.5px] uppercase tracking-[0.12em] font-semibold text-[#3A5275] mb-2">
                    {data.count} reassignment{data.count > 1 ? "s" : ""} · newest first
                  </div>
                  <ol
                    className="space-y-3 relative"
                    data-testid="audit-list"
                  >
                    {data.reassignments.map((r, idx) => (
                      <li
                        key={`${r.reassigned_at}-${idx}`}
                        className="rounded-lg border border-[#D6E3EF] bg-white px-3 py-2.5"
                        data-testid={`audit-row-${idx}`}
                      >
                        <div className="flex items-center gap-2 text-[12.5px] font-mono">
                          <span className="text-[#3A5275] truncate">{r.previous_rn || "(unassigned)"}</span>
                          <ArrowRight className="w-3.5 h-3.5 text-[#0A3D79] shrink-0" />
                          <span className="text-[#0B1E3A] font-semibold truncate">{r.new_rn || "—"}</span>
                        </div>
                        <div className="text-[11px] text-[#3A5275] mt-1">
                          {fmtTs(r.reassigned_at)}
                        </div>
                        {r.reason && (
                          <div className="mt-2 flex items-start gap-1.5 text-[12px] text-[#0B1E3A] bg-[#F7FAFD] rounded px-2 py-1.5 border border-[#E5EEF5]">
                            <MessageSquare className="w-3.5 h-3.5 text-[#8FB6D9] shrink-0 mt-0.5" />
                            <span className="italic">{r.reason}</span>
                          </div>
                        )}
                      </li>
                    ))}
                  </ol>
                </>
              )}
            </>
          )}
        </div>
      </aside>
    </div>
  );
}
